import axios from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const AUTH_TOAST_EXCLUDED_PATHS = ['/auth/login', '/system-admin/login'];
const isSystemAdminRequest = (url = '') => url.includes('/system-admin');

const showApiErrorToast = (message, options = {}) => {
  const toastId = `api-error:${message}`;
  toast.error(message, { id: toastId, ...options });
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const tokenKey = isSystemAdminRequest(config.url) ? 'superToken' : 'token';
    const token = localStorage.getItem(tokenKey);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      const requestUrl = error.config?.url || '';
      const skipToast = AUTH_TOAST_EXCLUDED_PATHS.some((path) => requestUrl.includes(path));
      const isSuperAdminApi = isSystemAdminRequest(requestUrl);
      
      if (status === 401) {
        if (isSuperAdminApi) {
          localStorage.removeItem('superToken');
          if (!skipToast) {
            window.location.hash = '#/system-admin';
          }
        } else {
          localStorage.removeItem('token');
          window.location.hash = '#/login';
        }

        if (!skipToast) {
          showApiErrorToast('Oturum süresi doldu. Lütfen yeniden giriş yapın.');
        }
      } else if (status === 403) {
        if (skipToast) {
          return Promise.reject(error);
        }

        if (isSuperAdminApi && (data.error || '').includes('Sadece Sistem Yöneticisi')) {
          localStorage.removeItem('superToken');
          window.location.hash = '#/system-admin';
        }

        if (data.isDemoLimit) {
          // Demo sınırı uyarısı
          showApiErrorToast(data.message || 'Demo sınırı aşıldı!', {
            duration: 6000,
            icon: '🔒'
          });
        } else {
          showApiErrorToast(data.error || 'Erişim reddedildi. Yetkiniz yetersiz.');
        }
      } else if (status >= 500) {
        if (!skipToast) {
          showApiErrorToast('Sunucu hatası. Lütfen daha sonra tekrar deneyin.');
        }
      } else if (data.error) {
        if (!skipToast) {
          showApiErrorToast(data.error);
        }
      }
    } else if (error.request) {
      showApiErrorToast('Ağ hatası. Bağlantınızı kontrol edin.');
    } else {
      showApiErrorToast('Beklenmeyen bir hata oluştu.');
    }
    
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  getCompanyProfile: () => api.get('/auth/company-profile'),
  updateCompanyProfile: (data) => api.put('/auth/company-profile', data),
};

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getSalesChart: () => api.get('/dashboard/sales-chart'),
  getExpensesChart: () => api.get('/dashboard/expenses-chart'),
  getRecentActivities: () => api.get('/dashboard/recent-activities'),
  getTopProducts: () => api.get('/dashboard/top-products'),
  getLowStock: () => api.get('/dashboard/low-stock'),
};

export const accountingAPI = {
  getInvoices: (params) => api.get('/accounting/invoices', { params }),
  getInvoice: (id) => api.get(`/accounting/invoices/${id}`),
  createInvoice: (data) => api.post('/accounting/invoices', data),
  updateInvoice: (id, data) => api.put(`/accounting/invoices/${id}`, data),
  getExpenses: (params) => api.get('/accounting/expenses', { params }),
  createExpense: (data) => api.post('/accounting/expenses', data),
  getReportSummary: (params) => api.get('/accounting/reports/summary', { params }),
  getCustomerBalances: () => api.get('/accounting/customer-balances'),
  getIrsaliyeler: (params) => api.get('/accounting/irsaliye', { params }),
  getIrsaliye: (id) => api.get(`/accounting/irsaliye/${id}`),
  createIrsaliye: (data) => api.post('/accounting/irsaliye', data),
  updateIrsaliye: (id, data) => api.put(`/accounting/irsaliye/${id}`, data),
  convertIrsaliyeToInvoice: (id, data) => api.post(`/accounting/irsaliye/${id}/convert-to-invoice`, data),
};

export const salesAPI = {
  getCustomers: (params) => api.get('/sales/customers', { params }),
  createCustomer: (data) => api.post('/sales/customers', data),
  updateCustomer: (id, data) => api.put(`/sales/customers/${id}`, data),
  deleteCustomer: (id) => api.delete(`/sales/customers/${id}`),
  getProducts: (params) => api.get('/sales/products', { params }),
  createProduct: (data) => api.post('/sales/products', data),
  getOrders: (params) => api.get('/sales/orders', { params }),
  createOrder: (data) => api.post('/sales/orders', data),
  updateOrder: (id, data) => api.put(`/sales/orders/${id}`, data),
};

export const marketingAPI = {
  getCampaigns: (params) => api.get('/marketing/campaigns', { params }),
  createCampaign: (data) => api.post('/marketing/campaigns', data),
  updateCampaign: (id, data) => api.put(`/marketing/campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/marketing/campaigns/${id}`),
  getCustomerSegments: () => api.get('/marketing/customer-segments'),
  getCustomerNotes: (params) => api.get('/marketing/customer-notes', { params }),
  createCustomerNote: (data) => api.post('/marketing/customer-notes', data),
  getReminders: () => api.get('/marketing/reminders'),
  getAnalytics: () => api.get('/marketing/analytics'),
};

export const productionAPI = {
  getOrders: (params) => api.get('/production/orders', { params }),
  createOrder: (data) => api.post('/production/orders', data),
  updateOrder: (id, data) => api.put(`/production/orders/${id}`, data),
  deleteOrder: (id) => api.delete(`/production/orders/${id}`),
  updateOrderStatus: (id, status) => api.patch(`/production/orders/${id}/status`, { status }),
  getStats: () => api.get('/production/stats'),
  getMaterialUsage: () => api.get('/production/material-usage'),
  getCostAnalysis: () => api.get('/production/cost-analysis'),
  getEfficiencyReport: () => api.get('/production/efficiency-report'),
  barcodeConsume: (data) => api.post('/production/barcode-consume', data),
};

export const shippingAPI = {
  getShipments: (params) => api.get('/shipping/shipments', { params }),
  createShipment: (data) => api.post('/shipping/shipments', data),
  updateShipment: (id, data) => api.put(`/shipping/shipments/${id}`, data),
  deleteShipment: (id) => api.delete(`/shipping/shipments/${id}`),
  getStats: () => api.get('/shipping/stats'),
  getByType: () => api.get('/shipping/by-type'),
  getDeliveryPerformance: () => api.get('/shipping/delivery-performance'),
  getTracking: (trackingNumber) => api.get(`/shipping/tracking/${trackingNumber}`),
  getDelayedShipments: () => api.get('/shipping/delayed-shipments'),
};

export const stockAPI = {
  // Kategoriler
  getCategories: () => api.get('/stock/categories'),
  createCategory: (data) => api.post('/stock/categories', data),
  updateCategory: (id, data) => api.put(`/stock/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/stock/categories/${id}`),
  
  // Depolar
  getWarehouses: () => api.get('/stock/warehouses'),
  createWarehouse: (data) => api.post('/stock/warehouses', data),
  updateWarehouse: (id, data) => api.put(`/stock/warehouses/${id}`, data),
  deleteWarehouse: (id) => api.delete(`/stock/warehouses/${id}`),
  
  // Hammaddeler
  getMaterials: (params) => api.get('/stock/materials', { params }),
  getMaterial: (id) => api.get(`/stock/materials/${id}`),
  getMaterialByBarcode: (barcode) => api.get(`/stock/materials/by-barcode/${encodeURIComponent(barcode)}`),
  createMaterial: (data) => api.post('/stock/materials', data),
  updateMaterial: (id, data) => api.put(`/stock/materials/${id}`, data),
  deleteMaterial: (id) => api.delete(`/stock/materials/${id}`),
  
  // Stok Hareketleri
  getMovements: (params) => api.get('/stock/movements', { params }),
  createMovement: (data) => api.post('/stock/movements', data),
  barcodeScan: (data) => api.post('/stock/barcode-scan', data),
  
  // Özet
  getSummary: () => api.get('/stock/summary'),
};

export const hrAPI = {
  // Personel
  getEmployees: () => api.get('/hr/employees'),
  getEmployee: (id) => api.get(`/hr/employees/${id}`),
  createEmployee: (data) => api.post('/hr/employees', data),
  updateEmployee: (id, data) => api.put(`/hr/employees/${id}`, data),
  deleteEmployee: (id) => api.delete(`/hr/employees/${id}`),
  getEmployeeStats: () => api.get('/hr/employees-stats'),
  
  // İzinler
  getLeaveRequests: (params) => api.get('/hr/leave-requests', { params }),
  createLeaveRequest: (data) => api.post('/hr/leave-requests', data),
  updateLeaveRequest: (id, data) => api.put(`/hr/leave-requests/${id}`, data),
  
  // Devam
  getAttendance: (params) => api.get('/hr/attendance', { params }),
  getTodayAttendance: () => api.get('/hr/attendance/today'),
  createAttendance: (data) => api.post('/hr/attendance', data),
  getAttendanceStats: () => api.get('/hr/attendance/stats'),
  
  // Roller ve İzinler
  getRoles: () => api.get('/hr/roles'),
  createRole: (data) => api.post('/hr/roles', data),
  updateRole: (id, data) => api.put(`/hr/roles/${id}`, data),
  deleteRole: (id) => api.delete(`/hr/roles/${id}`),
  
  // Sistem Ayarları
  getSettings: () => api.get('/hr/settings'),
  updateSettings: (data) => api.put('/hr/settings', data),
};

export const ordersAPI = {
  getOrders: (params) => api.get('/orders/orders', { params }),
  getOrder: (id) => api.get(`/orders/orders/${id}`),
  createOrder: (data) => api.post('/orders/orders', data),
  updateOrder: (id, data) => api.put(`/orders/orders/${id}`, data),
  deleteOrder: (id) => api.delete(`/orders/orders/${id}`),
  updateOrderStatus: (id, status) => api.patch(`/orders/orders/${id}/status`, { status }),
  getOrderStats: () => api.get('/orders/orders-stats'),
};

export const usersAPI = {
  getUsers: () => api.get('/users'),
  getUser: (id) => api.get(`/users/${id}`),
  createUser: (data) => api.post('/users', data),
  updateUser: (id, data) => api.put(`/users/${id}`, data),
  deleteUser: (id) => api.delete(`/users/${id}`),
  getMyPermissions: () => api.get('/users/my/permissions'),
  getPermissionsList: () => api.get('/users/permissions/list'),
  getRolesList: () => api.get('/users/roles/list'),
};

export const messagesAPI = {
  getInbox: () => api.get('/messages/inbox'),
  getSent: () => api.get('/messages/sent'),
  getAdminAll: () => api.get('/messages/admin/all'),
  sendMessage: (data) => api.post('/messages', data),
  markAsRead: (id) => api.put(`/messages/${id}/read`),
  deleteMessage: (id) => api.delete(`/messages/${id}`),
};

export const notificationsAPI = {
  getNotifications: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  toggleImportant: (id) => api.put(`/notifications/${id}/important`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  deleteNotification: (id) => api.delete(`/notifications/${id}`),
};

export const systemAdminAPI = {
  login: (credentials) => api.post('/system-admin/login', credentials),
  getProfile: () => api.get('/system-admin/profile'),
  updateProfile: (data) => api.put('/system-admin/profile', data),
  getCompanies: () => api.get('/system-admin/companies'),
  createCompany: (data) => api.post('/system-admin/companies', data),
  extendLicense: (id, data) => api.post(`/system-admin/companies/${id}/extend`, data),
  sendLicenseWarning: (id, data) => api.post(`/system-admin/companies/${id}/send-license-warning`, data),
  updateCompanyStatus: (id, data) => api.patch(`/system-admin/companies/${id}/status`, data),
  resetCompanyPassword: (id, data) => api.post(`/system-admin/companies/${id}/reset-password`, data),
  deleteCompany: (id) => api.delete(`/system-admin/companies/${id}`),
};

export default api;
